import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useUnreadMessagesContext } from "@/contexts/UnreadMessagesContext";
import { globalRealtimeManager } from "@/services/globalRealtimeManager";
import { useAuth } from "@/hooks/useAuth";
import { useAdminRole } from "@/hooks/useAdminRole";
import { Navigate } from "react-router-dom";
import RightSideMenu from "@/components/navigation/RightSideMenu";
import AdPanel from "@/components/ads/AdPanel";
import { Activity, Zap, RefreshCw, CheckCircle, XCircle, Clock } from "lucide-react";
import { AlphaLoader } from "@/components/ui/alpha-loader";

const AdminMonitoring = () => {
  const { user } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdminRole();

  if (adminLoading) {
    return (
      <div className="min-h-screen bg-carbon flex items-center justify-center">
        <AlphaLoader size="large" />
      </div>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/discovery" replace />;
  }

  const { unreadCount, forceSync, getConnectionHealth } = useUnreadMessagesContext();
  const [connectionHealth, setConnectionHealth] = useState<Record<string, any>>({});
  const [refreshing, setRefreshing] = useState(false);

  const refreshHealth = () => {
    setRefreshing(true);
    const health = getConnectionHealth();
    setConnectionHealth(health);
    setTimeout(() => setRefreshing(false), 500);
  };

  const handleForceSync = () => {
    console.log("🔄 Admin triggered force sync");
    forceSync();
    setTimeout(refreshHealth, 1000);
  };

  const handleNuclearReset = () => {
    if (user) {
      console.log("🚨 Admin triggered nuclear reset");
      globalRealtimeManager.nuclearReset(user.id);
      setTimeout(refreshHealth, 1000);
    }
  };

  useEffect(() => {
    refreshHealth();
    // Auto-refresh every 10 seconds
    const interval = setInterval(refreshHealth, 10000);
    return () => clearInterval(interval);
  }, []);

  const healthEntries = Object.entries(connectionHealth);
  const healthyConnections = healthEntries.filter(([_, health]) => health.isHealthy).length;
  const totalConnections = healthEntries.length;

  return (
    <div className="min-h-screen bg-carbon">
      <RightSideMenu />
      <AdPanel />
      
      <div className="p-4 max-w-6xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-ice-white mb-2 flex items-center justify-center gap-2">
            <Activity className="w-8 h-8 text-neon-red" />
            System Monitoring
          </h1>
          <p className="text-ice-white/60">Real-time connection health and system status</p>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-carbon border-ice-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-ice-white/60 text-sm">Unread Messages</p>
                  <p className="text-2xl font-bold text-ice-white">{unreadCount}</p>
                </div>
                <div className="text-neon-red">
                  <Activity className="w-8 h-8" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-carbon border-ice-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-ice-white/60 text-sm">Healthy Connections</p>
                  <p className="text-2xl font-bold text-ice-white">
                    {healthyConnections}/{totalConnections}
                  </p>
                </div>
                <div className={healthyConnections === totalConnections ? "text-toxic-green" : "text-neon-red"}>
                  {healthyConnections === totalConnections ? (
                    <CheckCircle className="w-8 h-8" />
                  ) : (
                    <XCircle className="w-8 h-8" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-carbon border-ice-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-ice-white/60 text-sm">Last Refresh</p>
                  <p className="text-sm text-ice-white">
                    {new Date().toLocaleTimeString()}
                  </p>
                </div>
                <div className="text-electric-violet">
                  <Clock className="w-8 h-8" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Control Panel */}
        <Card className="bg-carbon border-ice-white/10 mb-6">
          <CardHeader>
            <CardTitle className="text-ice-white flex items-center gap-2">
              <Zap className="w-5 h-5 text-neon-red" />
              Emergency Controls
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={refreshHealth}
                disabled={refreshing}
                variant="outline"
                className="border-ice-white/20 text-ice-white hover:bg-ice-white/10"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh Status
              </Button>
              
              <Button
                onClick={handleForceSync}
                className="bg-electric-violet hover:bg-electric-violet/90 text-ice-white"
              >
                <Zap className="w-4 h-4 mr-2" />
                Force Sync
              </Button>
              
              <Button
                onClick={handleNuclearReset}
                variant="destructive"
                className="bg-neon-red hover:bg-neon-red/90"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Nuclear Reset
              </Button>
            </div>
            <p className="text-ice-white/60 text-sm mt-3">
              <strong>Force Sync:</strong> Reloads data and reconnects channels. 
              <strong className="ml-4">Nuclear Reset:</strong> Destroys all connections and forces clean restart.
            </p>
          </CardContent>
        </Card>

        {/* Connection Health Details */}
        <Card className="bg-carbon border-ice-white/10">
          <CardHeader>
            <CardTitle className="text-ice-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-neon-red" />
              Connection Health Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            {healthEntries.length === 0 ? (
              <div className="text-center py-8">
                <Activity className="w-16 h-16 text-ice-white/40 mx-auto mb-4" />
                <p className="text-ice-white/60">No active connections</p>
              </div>
            ) : (
              <div className="space-y-4">
                {healthEntries.map(([channelId, health]) => (
                  <div key={channelId} className="border border-ice-white/10 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-ice-white">{channelId}</h3>
                        <Badge
                          variant={health.isHealthy ? "default" : "destructive"}
                          className={health.isHealthy ? "bg-toxic-green text-carbon" : "bg-neon-red text-ice-white"}
                        >
                          {health.isHealthy ? "Healthy" : "Unhealthy"}
                        </Badge>
                        <Badge variant="outline" className="border-ice-white/20 text-ice-white">
                          {health.type}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-ice-white/60">Retry Count</p>
                        <p className="text-ice-white font-medium">{health.retryCount}</p>
                      </div>
                      <div>
                        <p className="text-ice-white/60">Callbacks</p>
                        <p className="text-ice-white font-medium">{health.callbackCount}</p>
                      </div>
                      <div>
                        <p className="text-ice-white/60">Last Connected</p>
                        <p className="text-ice-white font-medium">
                          {new Date(health.lastConnected).toLocaleTimeString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-ice-white/60">Status</p>
                        <div className="flex items-center gap-2">
                          {health.isHealthy ? (
                            <CheckCircle className="w-4 h-4 text-toxic-green" />
                          ) : (
                            <XCircle className="w-4 h-4 text-neon-red" />
                          )}
                          <span className="text-ice-white font-medium">
                            {health.isHealthy ? "Connected" : "Disconnected"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminMonitoring;